import React, { useState, useEffect } from 'react';
import { Row, Col, Pagination, Input, Button, Empty } from 'antd';
import { SearchOutlined, PlusOutlined, BookOutlined, TagsOutlined, CalendarOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import PostCard from '../../components/PostCard';
import { posts } from '../../api/mockData';
import './index.css';

const { Search } = Input;

const Home = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [currentPosts, setCurrentPosts] = useState(posts);
  const pageSize = 8;

  useEffect(() => {
    setCurrentPosts(posts);
  }, []);

  const handleDelete = (id) => {
    const filteredPosts = currentPosts.filter(post => post.id !== id);
    setCurrentPosts(filteredPosts);
  };

  const filteredPosts = currentPosts.filter(post => {
    if (!post) return false;
    
    const titleMatch = post.title?.toLowerCase().includes(searchText.toLowerCase());
    const tagMatch = post.tags?.some(tag => tag?.toLowerCase().includes(searchText.toLowerCase()));
    const summaryMatch = post.summary?.toLowerCase().includes(searchText.toLowerCase());
    
    return titleMatch || tagMatch || summaryMatch;
  });

  const displayedPosts = filteredPosts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 计算统计数据
  const totalTags = new Set(currentPosts.flatMap(post => post.tags || [])).size;
  
  return (
    <div className="home-page">
      {/* 背景装饰 */}
      <div className="background-decoration">文</div>

      <div className="page-header">
        <div className="header-content">
          <h2 className="page-title">
            <span className="chinese-seal-small">典</span>
            文物典藏
          </h2>
          <p className="page-subtitle">每一件文物，都是历史的见证者，每一段故事，都是文明的记忆</p>
        </div>
        <div className="header-actions">
          <Search
            placeholder="搜索文物、标签或内容..."
            allowClear
            enterButton={<SearchOutlined />}
            className="search-input"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
            size="large"
          />
          <Link to="/write">
            <Button type="primary" icon={<PlusOutlined />} className="write-btn" size="large">
              记录文物
            </Button>
          </Link>
        </div>
      </div>

      <div className="antique-divider">
        <div className="divider-line"></div>
        <div className="divider-icon">🏺</div>
        <div className="divider-line"></div>
      </div>

      {/* 统计信息 */}
      <div className="stats-info">
        <div className="stat-item">
          <BookOutlined className="stat-icon" />
          <div className="stat-content">
            <span className="stat-number">{currentPosts.length}</span>
            <span className="stat-label">件文物</span>
          </div>
        </div>
        <div className="stat-item">
          <TagsOutlined className="stat-icon" />
          <div className="stat-content">
            <span className="stat-number">{totalTags}</span>
            <span className="stat-label">个标签</span>
          </div>
        </div>
        <div className="stat-item">
          <CalendarOutlined className="stat-icon" />
          <div className="stat-content">
            <span className="stat-number">
              {new Set(currentPosts.map(p => p.date?.split('-')[0])).size}
            </span>
            <span className="stat-label">个年份</span>
          </div>
        </div>
      </div>

      {/* 文章列表 */}
      {displayedPosts.length > 0 ? (
        <>
          <Row gutter={[30, 30]} className="posts-grid">
            {displayedPosts.map(post => (
              <Col key={post.id} xs={24} sm={12} lg={8} xl={6}>
                <PostCard post={post} onDelete={handleDelete} />
              </Col>
            ))}
          </Row>

          {/* 分页 */}
          {filteredPosts.length > pageSize && (
            <div className="pagination-container">
              <Pagination
                current={currentPage}
                total={filteredPosts.length}
                pageSize={pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) => 
                  <span style={{ fontFamily: '"STKaiti", serif', color: '#5a3921' }}>
                    第 {range[0]}-{range[1]} 件 / 共 {total} 件文物
                  </span>
                }
                className="antique-pagination"
              />
            </div>
          )}
        </>
      ) : (
        <div className="no-data">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div className="empty-description">
                <h3>暂未发现相关文物</h3>
                <p>换个关键词试试，或成为第一个记录这件文物的人</p>
              </div>
            }
            className="empty-state"
          >
            {searchText && (
              <Button 
                onClick={() => {
                  setSearchText('');
                  setCurrentPage(1);
                }}
                className="clear-search-btn"
              >
                清除搜索条件
              </Button>
            )}
            <Link to="/write">
              <Button type="primary" icon={<PlusOutlined />} size="large">
                记录新的文物
              </Button>
            </Link>
          </Empty>
        </div>
      )}

      {/* 快速指引 */}
      {displayedPosts.length > 0 && (
        <div className="antique-divider" style={{ marginTop: '60px' }}>
          <div className="divider-line"></div>
          <div className="divider-icon">📚</div>
          <div className="divider-line"></div>
        </div>
      )}

      {/* 引导卡片 */}
      {displayedPosts.length > 0 && (
        <Row gutter={[30, 30]} style={{ marginTop: '40px' }}>
          <Col xs={24} md={12}>
            <div className="stat-item" style={{ textAlign: 'center', cursor: 'pointer' }}>
              <Link to="/archive" style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>📖</div>
                <h3 style={{ color: '#5a3921', marginBottom: '10px' }}>查看文物归档</h3>
                <p style={{ color: '#7d5d3b' }}>按时间脉络梳理文明记忆</p>
              </Link>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="stat-item" style={{ textAlign: 'center', cursor: 'pointer' }}>
              <Link to="/about" style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>👤</div>
                <h3 style={{ color: '#5a3921', marginBottom: '10px' }}>我的空间</h3>
                <p style={{ color: '#7d5d3b' }}>管理个人信息和收藏</p>
              </Link>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Home;